'use client';

import {useState, useEffect, useCallback} from 'react';
import {useLocale, useTranslations} from 'next-intl';
import Card from '@/components/ui/Card';

interface User {
  id: number;
  nom: string;
  email: string;
  role: string;
  gouvernorat_id: number | null;
  delegation_id: number | null;
  actif: boolean;
  force_password_change: boolean;
  created_at: string | null;
}

const ROLES = [
  {value: 'contributeur_local', label: 'Agent Local'},
  {value: 'valideur_regional', label: 'Chef Regional'},
  {value: 'valideur_central', label: 'Valideur Central'},
  {value: 'admin_contenu', label: 'Admin Contenu'},
  {value: 'admin_technique', label: 'Directeur General'},
  {value: 'auditeur', label: 'Auditeur'},
  {value: 'promoteur', label: 'Promoteur'},
];

const roleLabel = (role: string) => ROLES.find(r => r.value === role)?.label || role;

const roleBadgeColor: Record<string, string> = {
  admin_technique: 'bg-red-100 text-red-700',
  admin_contenu: 'bg-orange-100 text-orange-700',
  valideur_central: 'bg-blue-100 text-blue-700',
  valideur_regional: 'bg-indigo-100 text-indigo-700',
  contributeur_local: 'bg-green-100 text-green-700',
  auditeur: 'bg-gray-100 text-gray-600',
  promoteur: 'bg-purple-100 text-purple-700',
};

export default function GestionUtilisateursPage() {
  const locale = useLocale();
  const t = useTranslations('admin');
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterActif, setFilterActif] = useState('');
  const [showModal, setShowModal] = useState<'create' | 'edit' | null>(null);
  const [editUser, setEditUser] = useState<User | null>(null);
  const [formData, setFormData] = useState({nom: '', email: '', role: 'contributeur_local', gouvernorat_id: '', delegation_id: ''});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{type: 'success' | 'error'; text: string} | null>(null);
  const [tempPassword, setTempPassword] = useState<string | null>(null);

  const apiUrl = process.env.NEXT_PUBLIC_API_URL || '';
  const getHeaders = (): Record<string, string> => {
    const token = localStorage.getItem('utss_token');
    const h: Record<string, string> = {'Content-Type': 'application/json'};
    if (token) h.Authorization = `Bearer ${token}`;
    return h;
  };

  const loadUsers = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (filterRole) params.set('role', filterRole);
    if (filterActif) params.set('actif', filterActif);
    fetch(`${apiUrl}/api/v1/admin/utilisateurs?${params}`, {headers: getHeaders()})
      .then(r => r.ok ? r.json() : [])
      .then(data => setUsers(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [search, filterRole, filterActif, apiUrl]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const openCreate = () => {
    setFormData({nom: '', email: '', role: 'contributeur_local', gouvernorat_id: '', delegation_id: ''});
    setEditUser(null);
    setTempPassword(null);
    setShowModal('create');
  };

  const openEdit = (u: User) => {
    setFormData({
      nom: u.nom,
      email: u.email,
      role: u.role,
      gouvernorat_id: u.gouvernorat_id ? String(u.gouvernorat_id) : '',
      delegation_id: u.delegation_id ? String(u.delegation_id) : '',
    });
    setEditUser(u);
    setTempPassword(null);
    setShowModal('edit');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      const body: any = {
        nom: formData.nom,
        email: formData.email,
        role: formData.role,
      };
      if (formData.gouvernorat_id) body.gouvernorat_id = Number(formData.gouvernorat_id);
      if (formData.delegation_id) body.delegation_id = Number(formData.delegation_id);

      const url = showModal === 'create'
        ? `${apiUrl}/api/v1/admin/utilisateurs`
        : `${apiUrl}/api/v1/admin/utilisateurs/${editUser!.id}`;
      const method = showModal === 'create' ? 'POST' : 'PATCH';

      const res = await fetch(url, {method, headers: getHeaders(), body: JSON.stringify(body)});
      if (!res.ok) {
        const err = await res.json().catch(() => ({detail: 'Erreur'}));
        throw new Error(err.detail || 'Erreur');
      }
      setMessage({type: 'success', text: showModal === 'create' ? t('user_created') : t('user_updated')});
      setShowModal(null);
      loadUsers();
    } catch (err: any) {
      setMessage({type: 'error', text: err.message});
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (u: User) => {
    setMessage(null);
    const res = await fetch(`${apiUrl}/api/v1/admin/utilisateurs/${u.id}`, {
      method: 'DELETE', headers: getHeaders(),
    });
    if (res.ok) {
      setMessage({type: 'success', text: u.actif ? t('user_deactivated') : t('user_activated')});
      loadUsers();
    }
  };

  const handleResetPassword = async (u: User) => {
    setMessage(null);
    const res = await fetch(`${apiUrl}/api/v1/admin/utilisateurs/${u.id}/reset-password`, {
      method: 'POST', headers: getHeaders(),
    });
    if (res.ok) {
      const data = await res.json();
      setTempPassword(data.temporary_password);
      setEditUser(u);
      setMessage({type: 'success', text: t('password_reset_done')});
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('users_title')}</h1>
          <p className="text-sm text-gray-500">{users.length} {t('users_count')}</p>
        </div>
        <button
          onClick={openCreate}
          className="inline-flex items-center gap-2 bg-primary text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors cursor-pointer"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
          {t('add_user')}
        </button>
      </div>

      {/* Message */}
      {message && (
        <div className={`mb-4 px-4 py-3 rounded-lg text-sm font-medium ${message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
          {message.text}
          <button onClick={() => setMessage(null)} className="float-right text-lg leading-none cursor-pointer">&times;</button>
        </div>
      )}

      {/* Temp password display */}
      {tempPassword && editUser && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-amber-50 border border-amber-200">
          <p className="text-sm font-semibold text-amber-800">{t('temp_password_for')} {editUser.nom}:</p>
          <div className="flex items-center gap-3 mt-1">
            <code className="bg-white px-3 py-1 rounded border text-lg font-mono tracking-wider">{tempPassword}</code>
            <button
              onClick={() => { navigator.clipboard.writeText(tempPassword); setMessage({type: 'success', text: t('copied')}); }}
              className="text-xs text-primary hover:underline cursor-pointer"
            >{t('copy')}</button>
          </div>
          <p className="text-xs text-amber-600 mt-1">{t('temp_password_warning')}</p>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4">
        <input
          type="text"
          placeholder={t('search_placeholder')}
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white w-64 focus:ring-1 focus:ring-primary focus:border-primary outline-none"
        />
        <select
          value={filterRole}
          onChange={e => setFilterRole(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white"
        >
          <option value="">{t('all_roles')}</option>
          {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
        <select
          value={filterActif}
          onChange={e => setFilterActif(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white"
        >
          <option value="">{t('all_status')}</option>
          <option value="true">{t('active_only')}</option>
          <option value="false">{t('inactive_only')}</option>
        </select>
        {(search || filterRole || filterActif) && (
          <button onClick={() => { setSearch(''); setFilterRole(''); setFilterActif(''); }} className="text-xs text-gray-500 hover:text-primary cursor-pointer">
            {t('reset_filters')}
          </button>
        )}
      </div>

      {/* Table */}
      <Card>
        {loading ? (
          <div className="text-center py-8 text-gray-400">{t('loading')}</div>
        ) : users.length === 0 ? (
          <div className="text-center py-8 text-gray-400">{t('no_users')}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-xs text-gray-500 uppercase tracking-wider">
                  <th className="text-start py-3 px-3">ID</th>
                  <th className="text-start py-3 px-3">{t('col_name')}</th>
                  <th className="text-start py-3 px-3">{t('col_email')}</th>
                  <th className="text-start py-3 px-3">{t('col_role')}</th>
                  <th className="text-center py-3 px-3">{t('col_status')}</th>
                  <th className="text-start py-3 px-3">{t('col_created')}</th>
                  <th className="text-center py-3 px-3">{t('col_actions')}</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50/50">
                    <td className="py-3 px-3 text-xs text-gray-400">{u.id}</td>
                    <td className="py-3 px-3 font-medium text-gray-900">{u.nom}</td>
                    <td className="py-3 px-3 text-xs text-gray-600">{u.email}</td>
                    <td className="py-3 px-3">
                      <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${roleBadgeColor[u.role] || 'bg-gray-100 text-gray-600'}`}>
                        {roleLabel(u.role)}
                      </span>
                    </td>
                    <td className="py-3 px-3 text-center">
                      {u.actif
                        ? <span className="inline-flex items-center gap-1 text-xs text-green-600"><span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>{t('active')}</span>
                        : <span className="inline-flex items-center gap-1 text-xs text-red-500"><span className="w-1.5 h-1.5 rounded-full bg-red-400"></span>{t('inactive')}</span>
                      }
                    </td>
                    <td className="py-3 px-3 text-xs text-gray-400">{u.created_at ? new Date(u.created_at).toLocaleDateString('fr-TN') : '—'}</td>
                    <td className="py-3 px-3">
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => openEdit(u)} title={t('edit')} className="p-1.5 rounded hover:bg-blue-50 text-blue-600 cursor-pointer">
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                        </button>
                        <button onClick={() => handleResetPassword(u)} title={t('reset_password')} className="p-1.5 rounded hover:bg-amber-50 text-amber-600 cursor-pointer">
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" /></svg>
                        </button>
                        <button onClick={() => handleToggleActive(u)} title={u.actif ? t('deactivate') : t('activate')} className={`p-1.5 rounded cursor-pointer ${u.actif ? 'hover:bg-red-50 text-red-500' : 'hover:bg-green-50 text-green-600'}`}>
                          {u.actif
                            ? <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" /></svg>
                            : <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                          }
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowModal(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              {showModal === 'create' ? t('create_user_title') : t('edit_user_title')}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('col_name')}</label>
                <input
                  type="text"
                  required
                  value={formData.nom}
                  onChange={e => setFormData({...formData, nom: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-1 focus:ring-primary focus:border-primary outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('col_email')}</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={e => setFormData({...formData, email: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-1 focus:ring-primary focus:border-primary outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('col_role')}</label>
                <select
                  value={formData.role}
                  onChange={e => setFormData({...formData, role: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-1 focus:ring-primary focus:border-primary outline-none"
                >
                  {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('gouvernorat_id')}</label>
                  <input
                    type="number"
                    value={formData.gouvernorat_id}
                    onChange={e => setFormData({...formData, gouvernorat_id: e.target.value})}
                    placeholder="ID"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-1 focus:ring-primary focus:border-primary outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('delegation_id')}</label>
                  <input
                    type="number"
                    value={formData.delegation_id}
                    onChange={e => setFormData({...formData, delegation_id: e.target.value})}
                    placeholder="ID"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-1 focus:ring-primary focus:border-primary outline-none"
                  />
                </div>
              </div>
              {showModal === 'create' && (
                <p className="text-xs text-gray-500 bg-gray-50 rounded-lg p-3">
                  {t('auto_password_info')}
                </p>
              )}
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowModal(null)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                  {t('cancel')}
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-5 py-2 text-sm font-semibold bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 cursor-pointer"
                >
                  {saving ? t('saving') : showModal === 'create' ? t('create') : t('save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
